import { createSignal, For, Show } from "solid-js";
import { ChevronDown, Sparkles, TrendingUp, Wrench } from "lucide-solid";
import { Dialog, DialogContent } from "@/shared/ui/dialog";
import { t, locale } from "@/shared/i18n";
import {
  closeReleaseNotes,
  releaseNotesState,
  unseenReleases,
} from "@/shared/stores/releaseNotesStore";
import type { Release, ReleaseEntryKind, ReleaseNoteEntry } from "@/api/client";

/**
 * What changed in the app, in the words of whoever changed it.
 *
 * <p>Opens by itself the first time somebody visits the dashboard after an update that announces
 * something, showing only what arrived since they last looked — the announced entries first, the rest
 * folded behind a toggle; reopens from the profile menu on the whole history, unfolded. Which of the two
 * it is showing is the store's to say — see releaseNotesStore.</p>
 *
 * <p>The three kinds are told apart by badge rather than by section. A release is usually a handful of
 * lines, and grouping five things under three headings makes a list of five look like a document.</p>
 */
export default function WhatsNewDialog() {
  const shown = () =>
    releaseNotesState.showAll ? (releaseNotesState.notes?.releases ?? []) : unseenReleases();

  return (
    <Dialog open={releaseNotesState.open} onOpenChange={(open) => !open && closeReleaseNotes()}>
      <DialogContent class="max-w-xl">
        <div class="px-6 pt-6 pb-2">
          <div class="flex items-center gap-2">
            <Sparkles class="h-4 w-4 shrink-0 text-[var(--text-accent)]" />
            <h3 class="text-lg font-semibold text-white">{t("whatsNew.title", "Nytt i appen")}</h3>
          </div>
          <p class="mt-1 text-sm text-zinc-400">
            {releaseNotesState.showAll
              ? t("whatsNew.leadHistory", "Alt som har endret seg, nyeste først.")
              : t("whatsNew.lead", "Dette har kommet siden sist du var innom.")}
          </p>
        </div>

        <div class="space-y-6 px-6 py-4">
          <Show
            when={shown().length > 0}
            fallback={
              <p class="text-sm text-zinc-500">
                {t("whatsNew.empty", "Ingen endringer å vise ennå.")}
              </p>
            }
          >
            <For each={shown()}>
              {(release) => <ReleaseBlock release={release} fold={!releaseNotesState.showAll} />}
            </For>
          </Show>
        </div>

        <div class="flex flex-row justify-end gap-2 px-6 pb-6 pt-2">
          <button
            type="button"
            onClick={closeReleaseNotes}
            class="h-9 rounded-lg border px-4 text-sm font-medium text-white transition-colors"
            style={{
              "background-color": "rgba(var(--accent-rgb),0.2)",
              "border-color": "rgba(var(--accent-rgb),0.4)",
            }}
          >
            {t("whatsNew.gotIt", "Greit")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One release: its version and date, then its entries.
 *
 * <p>Folded when the dialog opened by itself. The entries the release announces lead, and everything
 * else that went out with them waits behind one toggle — a release is every fix and tweak since the last
 * one, and an interruption that opens on all of them buries the few things that earned it. A release
 * that announces nothing, still unseen because nothing opened the dialog for it, is only its toggle.</p>
 */
function ReleaseBlock(props: { release: Release; fold: boolean }) {
  const [showOthers, setShowOthers] = createSignal(false);
  const lead = () =>
    props.fold ? props.release.entries.filter((e) => e.announce) : props.release.entries;
  const others = () => (props.fold ? props.release.entries.filter((e) => !e.announce) : []);

  return (
    <section>
      <div class="mb-3 flex items-baseline gap-2 border-b border-white/5 pb-2">
        <span class="text-xs font-medium uppercase tracking-wider text-zinc-400">
          {t("whatsNew.version", "Versjon")} {props.release.version}
        </span>
        <Show when={props.release.date}>
          {(date) => <span class="text-xs text-zinc-600">{formatDate(date())}</span>}
        </Show>
      </div>

      <Show when={lead().length > 0}>
        <ul class="space-y-4">
          <For each={lead()}>{(entry) => <EntryRow entry={entry} />}</For>
        </ul>
      </Show>

      <Show when={others().length > 0}>
        <button
          type="button"
          onClick={() => setShowOthers(!showOthers())}
          aria-expanded={showOthers()}
          class="flex items-center gap-1 text-xs text-zinc-400 transition-colors hover:text-white"
          classList={{ "mt-4": lead().length > 0 }}
        >
          {otherChanges(others().length)}
          <ChevronDown
            class="h-3 w-3 transition-transform"
            classList={{ "rotate-180": showOthers() }}
          />
        </button>
        <Show when={showOthers()}>
          <ul class="mt-4 space-y-4">
            <For each={others()}>{(entry) => <EntryRow entry={entry} />}</For>
          </ul>
        </Show>
      </Show>
    </section>
  );
}

/** "3 other changes". One has its own key: "1 other changes" reads as a mistake in either language. */
function otherChanges(n: number): string {
  const label =
    n === 1
      ? t("whatsNew.otherChange", "{n} annen endring")
      : t("whatsNew.otherChanges", "{n} andre endringer");
  return label.replace("{n}", String(n));
}

function EntryRow(props: { entry: ReleaseNoteEntry }) {
  const [showDetail, setShowDetail] = createSignal(false);
  const style = () => kindStyle(props.entry.kind);

  return (
    <li class="flex gap-3">
      <span
        class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
        classList={{ [style().chip]: true }}
        aria-hidden="true"
      >
        {style().icon()}
      </span>

      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium text-white">
          <span
            class="mr-2 align-middle text-[10px] font-semibold uppercase tracking-wider"
            classList={{ [style().label]: true }}
          >
            {t(style().key, style().fallback)}
          </span>
          {props.entry.title}
        </p>

        <Show when={props.entry.summary}>
          {(summary) => <p class="mt-1 text-sm leading-relaxed text-zinc-400">{summary()}</p>}
        </Show>

        {/* Only where there is something to show. A "Show me how" that unfolds nothing is worse than
            no affordance at all — the reader has to press it once to learn it was never worth it. */}
        <Show when={props.entry.detail}>
          {(detail) => (
            <>
              <button
                type="button"
                onClick={() => setShowDetail(!showDetail())}
                aria-expanded={showDetail()}
                class="mt-2 flex items-center gap-1 text-xs text-[var(--text-accent)] transition-opacity hover:opacity-80"
              >
                {showDetail()
                  ? t("whatsNew.hideHow", "Skjul")
                  : t("whatsNew.showHow", "Vis meg hvordan")}
                <ChevronDown
                  class="h-3 w-3 transition-transform"
                  classList={{ "rotate-180": showDetail() }}
                />
              </button>
              <Show when={showDetail()}>
                <div class="mt-2 rounded-lg border border-white/5 bg-white/[0.03] p-3">
                  <Detail text={detail()} />
                </div>
              </Show>
            </>
          )}
        </Show>
      </div>
    </li>
  );
}

/**
 * The instructions, as written in the release-notes file.
 *
 * <p>Lines beginning "- " become a list and everything else a paragraph. Deliberately not Markdown:
 * the whole vocabulary here is "steps" and "a sentence", and a renderer would buy emphasis and links
 * nobody asked for at the price of a dependency and an injection surface.</p>
 */
function Detail(props: { text: string }) {
  const blocks = () => {
    const lines = props.text.split("\n").map((l) => l.trim()).filter(Boolean);
    const out: { list: boolean; lines: string[] }[] = [];

    for (const line of lines) {
      const isItem = line.startsWith("- ");
      const text = isItem ? line.slice(2) : line;
      const last = out[out.length - 1];
      if (last && last.list === isItem) last.lines.push(text);
      else out.push({ list: isItem, lines: [text] });
    }

    return out;
  };

  return (
    <div class="space-y-2 text-sm leading-relaxed text-zinc-300">
      <For each={blocks()}>
        {(block) =>
          block.list ? (
            <ul class="ml-4 list-disc space-y-1 marker:text-zinc-600">
              <For each={block.lines}>{(line) => <li>{line}</li>}</For>
            </ul>
          ) : (
            <For each={block.lines}>{(line) => <p>{line}</p>}</For>
          )
        }
      </For>
    </div>
  );
}

/** The badge, icon and colour for each kind. Accent for news, and quieter colours for the rest. */
function kindStyle(kind: ReleaseEntryKind) {
  switch (kind) {
    case "feature":
      return {
        key: "whatsNew.kindFeature",
        fallback: "Nytt",
        chip: "bg-[var(--accent)]/15 text-[var(--text-accent)]",
        label: "text-[var(--text-accent)]",
        icon: () => <Sparkles class="h-3.5 w-3.5" />,
      };
    case "fix":
      return {
        key: "whatsNew.kindFix",
        fallback: "Rettet",
        chip: "bg-emerald-500/10 text-emerald-400",
        label: "text-emerald-400/90",
        icon: () => <Wrench class="h-3.5 w-3.5" />,
      };
    default:
      return {
        key: "whatsNew.kindImprovement",
        fallback: "Forbedret",
        chip: "bg-sky-500/10 text-sky-400",
        label: "text-sky-400/90",
        icon: () => <TrendingUp class="h-3.5 w-3.5" />,
      };
  }
}

/**
 * The release date in the reader's own conventions.
 *
 * <p>Falls back to the raw string rather than to "Invalid Date": the field is typed by hand in a JSON
 * file, and a typo should cost the reader a plain date, not a word that reads as a broken app.</p>
 */
function formatDate(date: string): string {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  return parsed.toLocaleDateString(locale() === "en" ? "en-GB" : "nb-NO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
